import { fetchObjections } from '@api/fetches/marketing-website/fetchObjections'
import InputField from '@components/shared/FormFields/InputField'
import { Label } from '@components/shared/FormFields/styles'
import FormMessage from '@components/shared/FormMessage'
import { GetAutocompleteOptions } from '@components/WozResearch/StreetInput/GetAutocompleteOptions'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import useMediaQuery from '@hooks/useMediaQuery'
import { useOnClickOutside } from '@hooks/useOnClickOutside'
import pushToDataLayer from '@logic/data-layers/pushToDataLayer'
import trackSearchEngineFill from '@logic/data-layers/woz-check/trackSearchEngineFill'
import { scrollLock } from '@utils/scrollLock'
import { Validator } from '@utils/validation'
import addressSearchRequired from '@utils/validation/addressSearchRequired'
import debounce from 'lodash.debounce'
import throttle from 'lodash.throttle'
import React, { ChangeEvent, FC, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { DropdownMenu, InputFieldWrapper } from './../WozCheckBox/styles'
import { AddressOption, BeforeCustomQueryProps } from './../WozCheckBox/types'
import MobileOverlay from './MobileOverlay'
import { AddressSearchData, AddressSearchProps } from './types'

const AddressSearch: FC<AddressSearchProps> = ({
  addTestAttribute = false,
  label,
  inline = false,
  embedFormOptions,
  value,
  onValue,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [connectionError, setConnectionError] = useState(false)
  const [isDropdownMenuShown, setIsDropdownMenuShown] = useState(false)
  const [query, setQuery] = useState<string | null>(null)
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([])
  const [userStartedTyping, setUserStartedTyping] = useState(false)
  const [autocompleteCache, setAutocompleteCache] = useState({})
  const [selectedOption, setSelectedOption] = useState<AddressOption | null>(null)
  const [focussedOption, setFocussedOption] = useState<number | null>(null)
  const [beforeCustomQuery, setBeforeCustomQuery] = useState<BeforeCustomQueryProps | null>(null)

  const inputFieldWrapperRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const autocompleteRef = useRef<HTMLInputElement | null>(null)
  const mobileAutocompleteRef = useRef<HTMLInputElement | null>(null)
  const autocompleteOptionsRef = useRef<HTMLUListElement | null>(null)
  const mobileAutocompleteOptionsRef = useRef<HTMLUListElement | null>(null)

  const isMobile = useMediaQuery('(max-width: 768px)')

  const form = useForm<AddressSearchData>({
    resolver: yupResolver(
      Validator({
        address_search: addressSearchRequired(),
      }),
    ),
  })

  const { register, handleSubmit, watch, setFocus, setValue, clearErrors } = form

  useEffect(() => {
    setValue('address_search', value || '')
  }, [value])

  const { ref: RHFAddressSearchRef, onChange: RHFOnChange, ...registerAddressSearch } = register('address_search')

  const handleInputChange = useCallback(
    (event: ChangeEvent) => {
      void RHFOnChange(event)

      clearErrors('address_search')
      const value = (event.target as HTMLInputElement).value

      setIsDropdownMenuShown(!!value)

      if (!userStartedTyping) {
        if (embedFormOptions) {
          pushToDataLayer({
            event: 'interaction',
            event_name: 'address_search_engine_fill',
            product: 'partner',
          })
        } else {
          trackSearchEngineFill()
        }
        setUserStartedTyping(true)
      }

      setFocussedOption(null)
      setBeforeCustomQuery(null)
      changeQuery(value)
    },
    [userStartedTyping],
  )

  const handleInputClick = useCallback((_event: MouseEvent<HTMLInputElement>) => {
    setIsDropdownMenuShown(true)
  }, [])

  useEffect(() => {
    if (!isDropdownMenuShown || !isMobile) {
      scrollLock.disable()
    }
  }, [isDropdownMenuShown, isMobile])

  const handleOptionClick = useCallback((option: AddressOption) => {
    setSelectedOption(option)
    setFocus('address_search')

    if (option['bag-id']) setIsDropdownMenuShown(false)
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && beforeCustomQuery) {
        event.preventDefault()
        changeQuery(beforeCustomQuery.query, false)
        setValue('address_search', beforeCustomQuery.query)
        setBeforeCustomQuery(null)
      }

      if (!addressOptions) {
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (focussedOption !== null && focussedOption > 0) {
          const newFocussedOption = focussedOption - 1
          setFocussedOption(newFocussedOption)
        } else {
          setFocussedOption(addressOptions.length - 1)
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()

        if (focussedOption !== null && focussedOption + 1 < addressOptions.length) {
          const newFocussedOption = focussedOption + 1
          setFocussedOption(newFocussedOption)
        } else {
          setFocussedOption(0)
        }
      } else if (event.key === 'Enter') {
        event.preventDefault()

        if (focussedOption !== null) {
          handleOptionSelected(addressOptions[focussedOption])
        }
      }
    },
    [beforeCustomQuery, addressOptions, focussedOption],
  )

  const handleOptionSelected = useCallback(
    (option: AddressOption) => {
      if (!option?.['case-ref']) {
        if (!beforeCustomQuery && query) {
          setBeforeCustomQuery({
            query: query.trim(),
            option: option,
          })
        }
        const { city, ...optionWithoutCity } = option
        setValue('address_search', `${formatAddress(optionWithoutCity).join(' ')} `)

        changeQuery(Object.values(option).join(' '), false)
      } else {
        if (option.street && option['house-number'] && option.postcode && option.city) {
          setValue(
            'address',
            `${option.street} ${option['house-number']}${
              option['house-number-addition'] ? `-${option['house-number-addition']}` : ''
            }, ${option['postcode']} ${option.city}`,
          )
        }
        setValue('dossier', option['case-ref'])

        setBeforeCustomQuery(null)
        void handleSubmit(onSubmit)()
      }
    },
    [beforeCustomQuery, query],
  )

  const onSubmit = useCallback((data: AddressSearchData) => {
    if (data.address) {
      onValue(data)
      setIsDropdownMenuShown(false)
    }
  }, [])

  const autocompleteSearch = useCallback(
    (
      query: string,
      autocompleteCache: Record<string, AddressOption[]>,
      beforeCustomQuery: BeforeCustomQueryProps | null,
    ) => {
      if (abortControllerRef?.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()

      const cached = autocompleteCache[query]
      if (cached) {
        return Promise.resolve(cached).then(results => {
          setAddressOptions(results)
        })
      }

      const customQueryArgs = beforeCustomQuery !== null ? beforeCustomQuery.option : {}

      const body: { 'search-query': string; 'aggregated-search'?: boolean } = {
        'search-query': query,
        ...customQueryArgs,
      }

      // @deprecated
      if (beforeCustomQuery !== null) {
        body['aggregated-search'] = true
      }

      setConnectionError(false)
      const fetchTimeout = setTimeout(() => {
        setIsLoading(true)
      }, 2000)

      fetchObjections(JSON.stringify(body), abortControllerRef.current?.signal)
        .then(async response => {
          setIsLoading(false)
          clearTimeout(fetchTimeout)

          if (response.status === 200) {
            const data = (await response.json()) as AddressOption[]
            setAddressOptions(data)

            autocompleteCache[query] = data
            setAutocompleteCache(autocompleteCache)
          } else {
            setConnectionError(true)
          }
        })
        .catch(error => {
          setIsLoading(false)
          clearTimeout(fetchTimeout)

          if (error.name === 'AbortError') {
            // todo set form message error
          } else {
            setConnectionError(true)
          }
        })
    },
    [selectedOption],
  )

  const formatAddress = useCallback((option: AddressOption) => {
    const infoArray = []

    const streetArray = []
    option.street && streetArray.push(`${option.street}`)
    option['house-number-addition'] && option['house-number']
      ? streetArray.push(`${option['house-number']}-${option['house-number-addition']}`)
      : option['house-number'] && streetArray.push(`${option['house-number']}`)
    infoArray.push(streetArray.join(' '))
    option.postcode && infoArray.push(`${option.postcode}`)
    option['city'] && infoArray.push(`${option.city}`)

    return infoArray.filter(n => n)
  }, [])

  const changeQuery = useCallback((value: string, shouldSetValue = true) => {
    if (shouldSetValue) {
      setValue('address_search', value)
    }

    setQuery(null)
    setSelectedOption(null)

    if (value.length < 4) {
      setAddressOptions([])
      return
    }

    if (!isMobile && autocompleteRef.current) {
      autocompleteRef.current.value = value
    }

    if (isMobile && mobileAutocompleteRef.current) {
      mobileAutocompleteRef.current.value = value
    }

    setQuery(value)
  }, [])

  const autocompleteSearchThrottled = useCallback(throttle(autocompleteSearch, 300), [])
  const autocompleteSearchDebounced = useCallback(debounce(autocompleteSearch, 300), [])

  useOnClickOutside(inputFieldWrapperRef, () => {
    if (!isMobile) {
      setIsDropdownMenuShown(false)
    }
  })

  useEffect(() => {
    if (query !== null) {
      if (query.length < 5 || query.endsWith(' ')) {
        void autocompleteSearchThrottled(query, autocompleteCache, beforeCustomQuery)
      } else {
        void autocompleteSearchDebounced(query, autocompleteCache, beforeCustomQuery)
      }
    }
  }, [query, autocompleteCache, beforeCustomQuery])

  useEffect(() => {
    if (selectedOption) {
      handleOptionSelected(selectedOption)
    } else if (!!watch('dossier')) {
      setValue('dossier', '')
    }
  }, [selectedOption])

  useEffect(() => {
    setFocussedOption(null)
  }, [addressOptions])

  useEffect(() => {
    if (focussedOption !== null) {
      const optionHeight = isMobile ? 48 : 60
      const baseOffset = 4 * optionHeight
      let offsetY = (focussedOption + 1) * optionHeight
      offsetY = offsetY > 0 ? offsetY - baseOffset : 0

      if (isMobile && mobileAutocompleteOptionsRef && mobileAutocompleteOptionsRef.current) {
        mobileAutocompleteOptionsRef.current.scroll({
          top: offsetY,
          left: 0,
        })
      } else if (autocompleteOptionsRef && autocompleteOptionsRef.current) {
        autocompleteOptionsRef.current.scroll({
          top: offsetY,
          left: 0,
        })
      }
    }
  }, [focussedOption])

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.readOnly = isMobile
    }
    if (!isMobile) {
      scrollLock.disable()
    }
  }, [isMobile])

  const labelProps = label
    ? {
        label: label,
      }
    : {}

  return (
    <>
      <Label>Adres</Label>
      <InputFieldWrapper inline={inline} ref={inputFieldWrapperRef}>
        {connectionError && (
          <FormMessage
            type="error"
            dense
            message="Door drukte is de WOZ Check helaas niet beschikbaar. Het probleem is binnen een paar minuten opgelost. Onze excuses voor het ongemak."
          />
        )}
        <InputField type="hidden" {...register('dossier')} />
        <InputField
          addTestAttribute={!!addTestAttribute}
          ref={e => {
            RHFAddressSearchRef(e)
            autocompleteRef.current = e
          }}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          onChange={handleInputChange}
          {...labelProps}
          {...registerAddressSearch}
          isLoading={isLoading}
        />
        {!isMobile && query && (
          <DropdownMenu ref={autocompleteOptionsRef} isDropdownMenuShown={isDropdownMenuShown}>
            <GetAutocompleteOptions
              addressOptions={addressOptions}
              watch={watch}
              focussedOption={focussedOption}
              setFocussedOption={setFocussedOption}
              handleOptionClick={handleOptionClick}
            />
          </DropdownMenu>
        )}
      </InputFieldWrapper>
      {isMobile && (
        <MobileOverlay
          form={form}
          isOpen={isDropdownMenuShown}
          onClose={() => setIsDropdownMenuShown(false)}
          mobileAutocompleteRef={mobileAutocompleteRef}
          addressOptions={addressOptions}
          changeQuery={changeQuery}
          setSelectedOption={setSelectedOption}
          handleInputChange={handleInputChange}
        />
      )}
    </>
  )
}

export default AddressSearch
